// src/pages/markets/board/api/useTraders.ts
//
// react-query already hands `signal` to the fetcher, but it only aborts a query
// nobody observes if something asks it to. A hover panel that closes unmounts
// its only observer and then — by default — leaves the request running to fill
// the cache. That is the wrong trade here: the reader sweeps across rows, and
// twenty abandoned trader requests starve the one they actually stopped on.
//
// So: `abortOnUnmount` cancels explicitly on teardown. The cached result of a
// *completed* fetch is kept (cancel is a no-op once resolved), which is what
// makes the card-hover prefetch still worth doing.

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBoardTraders } from './boardApi';
import { boardKeys } from './boardKeys';
import type { BoardTradersResponse } from './boardTypes';

const TRADERS_STALE_MS = 55_000;
const TRADERS_GC_MS = 5 * 60_000;

export function useTraders(
    marketId: string | null,
    group: string | null,
    enabled = true,
    abortOnUnmount = true
) {
    const qc = useQueryClient();
    const active = enabled && Boolean(marketId) && Boolean(group);

    const query = useQuery<BoardTradersResponse, Error>({
        queryKey: boardKeys.traders(marketId ?? '', group ?? ''),
        queryFn: ({ signal }) =>
            fetchBoardTraders(marketId as string, group as string, signal),
        enabled: active,
        staleTime: TRADERS_STALE_MS,
        gcTime: TRADERS_GC_MS,
        retry: false,
    });

    useEffect(() => {
        if (!active || !abortOnUnmount) return;
        const key = boardKeys.traders(marketId as string, group as string);
        return () => {
            // Only the last observer cancels. The entry-dot layer keys on the
            // same (market, group) as this panel and is not hover-scoped, so an
            // unconditional cancel here blanks its dots mid-flight.
            const query = qc
                .getQueryCache()
                .find({ queryKey: key, exact: true });
            if ((query?.getObserversCount() ?? 0) > 0) return;
            void qc.cancelQueries({ queryKey: key, exact: true });
        };
    }, [qc, marketId, group, active, abortOnUnmount]);

    return {
        data: query.data ?? null,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error ?? null,
        refetch: () => {
            void query.refetch();
        },
    };
}

export function usePrefetchTraders() {
    const qc = useQueryClient();
    return useCallback(
        (marketId: string | null, group: string | null) => {
            if (!marketId || !group) return;
            void qc.prefetchQuery({
                queryKey: boardKeys.traders(marketId, group),
                queryFn: ({ signal }) =>
                    fetchBoardTraders(marketId, group, signal),
                staleTime: TRADERS_STALE_MS,
                gcTime: TRADERS_GC_MS,
            });
        },
        [qc]
    );
}
